const TZ_KEY = 'dashboard.timezone';
const TZ_EVENT = 'dashboard:tz-changed';

export function getTimezone(): string {
  if (typeof localStorage === 'undefined') return '';
  return localStorage.getItem(TZ_KEY) || '';
}

export function setTimezone(tz: string): void {
  if (tz) localStorage.setItem(TZ_KEY, tz);
  else localStorage.removeItem(TZ_KEY);
  window.dispatchEvent(new CustomEvent(TZ_EVENT));
}

export function getBrowserTimezone(): string {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; }
  catch { return 'UTC'; }
}

export function effectiveTimezone(): string {
  return getTimezone() || getBrowserTimezone();
}

export function listTimezones(): string[] {
  const anyIntl = Intl as unknown as { supportedValuesOf?: (k: string) => string[] };
  if (typeof anyIntl.supportedValuesOf === 'function') {
    try { return anyIntl.supportedValuesOf('timeZone'); } catch { /* noop */ }
  }
  return ['UTC', 'Europe/London', 'Europe/Berlin', 'Europe/Moscow', 'Europe/Kyiv',
          'America/New_York', 'America/Chicago', 'America/Los_Angeles',
          'Asia/Dubai', 'Asia/Kolkata', 'Asia/Singapore', 'Asia/Tokyo', 'Australia/Sydney'];
}

/**
 * Parse a timestamp that is known to be UTC even when the string lacks 'Z'.
 * Accepts SQLite 'YYYY-MM-DD HH:MM:SS', ISO with/without Z, and date-only 'YYYY-MM-DD'.
 */
export function parseUtc(s: string | null | undefined): Date | null {
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return new Date(s + 'T00:00:00Z');
  let v = s.includes('T') ? s : s.replace(' ', 'T');
  if (!/(Z|[+-]\d{2}:?\d{2})$/i.test(v)) v += 'Z';
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

const DATETIME_OPTS: Intl.DateTimeFormatOptions = {
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit',
  hour12: false,
};

const DATE_OPTS: Intl.DateTimeFormatOptions = {
  year: 'numeric', month: '2-digit', day: '2-digit',
};

export function formatDateTime(s: string | null | undefined, fallback = '—'): string {
  const d = parseUtc(s);
  if (!d) return fallback;
  return d.toLocaleString(undefined, { ...DATETIME_OPTS, timeZone: effectiveTimezone() });
}

export function formatDate(s: string | null | undefined, fallback = '—'): string {
  const d = parseUtc(s);
  if (!d) return fallback;
  return d.toLocaleDateString(undefined, { ...DATE_OPTS, timeZone: effectiveTimezone() });
}

/** Day labels for charts grouped by UTC date — keep in UTC so groupings don't shift. */
export function formatChartDay(
  s: string | null | undefined,
  opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' },
  fallback = '',
): string {
  const d = parseUtc(s);
  if (!d) return fallback;
  return d.toLocaleDateString('en-US', { ...opts, timeZone: 'UTC' });
}

export function tzListener(cb: () => void): () => void {
  const handler = () => cb();
  window.addEventListener(TZ_EVENT, handler);
  return () => window.removeEventListener(TZ_EVENT, handler);
}
